import { defineStore, acceptHMRUpdate } from 'pinia';
import values from 'lodash/values';
import { createFrozenMap } from '@/vuex/utils';
import { useUserSettingsStore } from '@/stores/user-settings.store';
import {
    addItemToGroup,
    removeItemFromGroup,
    fetchGroups,
    fetchGroup,
    fetchGroupIsEmpty,
    deleteGroup,
    createGroup,
    updateGroup,
} from '@/vuex/modules/groups/groups.utils';

const itemTypeToModuleName: Record<string, string> = {
    page: 'pages',
    feature: 'features',
    guide: 'guides',
    trackType: 'trackTypes',
    vocItem: 'vocItems',
    journey: 'journeys',
    omnichannel: 'emails',
};

let loadAllPromise: { items: boolean; promise: Promise<void> } | null = null;

interface Group {
    id: string;
    items?: unknown[];
    [key: string]: unknown;
}

interface GroupsState {
    map: Record<string, Group>;
    error: unknown;
    idFilter: string[];
    fetching: boolean;
    saving: boolean;
    loadedAllGroups: boolean;
    loadedAllItems: boolean;
}

export function getInitialState (): GroupsState {
    return {
        map: {},
        error: null,
        idFilter: [],
        fetching: false,
        saving: false,
        loadedAllGroups: false,
        loadedAllItems: false,
    };
}

export const useGroupsStore = defineStore('groups', {
    state: getInitialState,
    getters: {
        list: (state): Group[] => values(state.map),
        storedUserSettingGroupFilter (): string[] {
            const savedGroupFilter = useUserSettingsStore().settingByName('groupFilter', true);

            return (savedGroupFilter?.value as string[]) || [];
        },
        groupById: (state) => (id: string): Group | undefined => state.map[id],
        groupByGuideId (): ({ guideId }: { guideId: string }) => Group | undefined {
            return ({ guideId }) => (this.list as Group[]).find((group) => (group.items as { itemId: string }[] | undefined)?.some((item) => item.itemId === guideId));
        },
        groupByEntityId (): (id: string) => Group | undefined {
            return (id) => (this.list as Group[]).find((group) => (group.items as { itemId: string }[] | undefined)?.some((item) => item.itemId === id));
        },
    },
    actions: {
        setMap ({ map }: { map: Record<string, Group> }) {
            const next: Record<string, Group> = {};
            for (const id of Object.keys(map)) {
                const incoming = map[id];
                const existing = this.map[id];
                if (!incoming.items && existing && existing.items) {
                    next[id] = Object.freeze({ ...incoming, items: existing.items }) as Group;
                } else {
                    next[id] = incoming;
                }
            }
            this.map = next;
        },
        setError (error: unknown) {
            this.error = error;
        },
        setFetching (fetching: boolean) {
            this.fetching = fetching;
        },
        setMapAtKey ({ id, group }: { id: string; group: Group }) {
            this.map[id] = group;
        },
        deleteMapKey ({ id }: { id: string }) {
            delete this.map[id];
        },
        setSaving (saving: boolean) {
            this.saving = saving;
        },
        setFilter ({ idFilter }: { idFilter: string[] }) {
            this.idFilter = idFilter;
        },
        async loadAll ({ reFetch = false, items = false }: { reFetch?: boolean; items?: boolean } = {}) {
            if (!reFetch) {
                const requestingItemsAndItemsAreLoaded = items && this.loadedAllItems;
                const requestingGroupsAndGroupsAreLoaded = !items && this.loadedAllGroups;

                if (requestingItemsAndItemsAreLoaded || requestingGroupsAndGroupsAreLoaded) {
                    return;
                }
            }

            if (loadAllPromise && (!items || loadAllPromise.items)) {
                await loadAllPromise.promise;

                return;
            }

            this.setFetching(true);

            let promise = null! as Promise<void>;
            promise = (async () => {
                try {
                    const [{ data: groups }] = await Promise.all([
                        fetchGroups({ items }),
                        useUserSettingsStore().loadOne({
                            namespaced: true,
                            name: 'groupFilter',
                        }),
                    ]);

                    const savedGroupFilter = this.storedUserSettingGroupFilter;

                    if (savedGroupFilter && savedGroupFilter.length) {
                        const validIds = new Set((groups as Group[]).map((group) => group.id));
                        const cleanedFilter = savedGroupFilter.filter((id) => validIds.has(id));

                        // Strip ids for product areas that no longer exist (e.g. deleted by another user)
                        // and persist the cleanup so stale ids don't keep returning on every load.
                        if (cleanedFilter.length !== savedGroupFilter.length) {
                            this.updateFilter({ idFilter: cleanedFilter });
                        } else {
                            this.setFilter({ idFilter: cleanedFilter });
                        }
                    }

                    this.setFetching(false);
                    this.setMap({
                        map: createFrozenMap(groups as Group[]) as Record<string, Group>,
                    });
                    this.loadedAllGroups = true;
                    this.loadedAllItems = items;
                } catch (error) {
                    this.setFetching(false);
                    this.setError(error);
                    throw error;
                } finally {
                    if (loadAllPromise && loadAllPromise.promise === promise) {
                        loadAllPromise = null;
                    }
                }
            })();

            loadAllPromise = { items, promise };
            await promise;
        },
        async loadOne ({ reFetch = false, ...payload }: { reFetch?: boolean; id: string; [key: string]: unknown }) {
            const { id } = payload;

            if (!reFetch && !!this.groupById(id as string)) return;

            this.setFetching(true);

            try {
                const [{ data: group }, { data: isEmptyResult }] = await Promise.all([
                    fetchGroup(payload as { id: string; items: boolean }),
                    fetchGroupIsEmpty({ id }),
                ]);
                (group as Group).isEmpty = (isEmptyResult as { isempty: boolean }).isempty;
                this.setFetching(false);
                this.setMapAtKey({
                    id: (group as Group).id,
                    group: Object.freeze(group) as Group,
                });
            } catch (error) {
                this.setFetching(false);
                this.setError(error);
                throw error;
            }
        },
        async remove ({ id }: { id: string }) {
            this.setFetching(true);

            try {
                await deleteGroup({ id });
                this.setFetching(false);
                this.deleteMapKey({ id });

                if (this.idFilter.includes(id)) {
                    this.updateFilter({ idFilter: this.idFilter.filter((filterId) => filterId !== id) });
                }
            } catch (error) {
                this.setFetching(false);
                this.setError(error);
                throw error;
            }
        },
        async create (payload: Record<string, unknown>) {
            try {
                this.setSaving(true);
                const result = await createGroup(payload);
                const group = result.data as Group;
                this.setMapAtKey({
                    id: group.id,
                    group: Object.freeze(group) as Group,
                });

                return group;
            } catch (error) {
                this.setError(error);
                throw error;
            } finally {
                this.setSaving(false);
            }
        },
        async update (payload: Record<string, unknown>) {
            try {
                this.setSaving(true);
                const result = await updateGroup(payload);
                const group = result.data as Group;
                this.setMapAtKey({
                    id: group.id,
                    group: Object.freeze(group) as Group,
                });
            } catch (error) {
                this.setError(error);
                throw error;
            } finally {
                this.setSaving(false);
            }
        },
        async addItemToGroup (payload: { groupId: string; itemType: string; itemId: string; previousGroupId?: string } = { groupId: '', itemType: '', itemId: '' }) {
            this.setSaving(true);
            const { groupId, itemType, itemId, previousGroupId } = payload;
            if (!groupId) return;

            // eslint-disable-next-line @typescript-eslint/no-require-imports
            const vuexStore = require('@/vuex/store').default;
            try {
                await addItemToGroup({ groupId, itemType, itemId });
                await vuexStore?.dispatch(
                    `${itemTypeToModuleName[itemType]}/loadOne`,
                    { id: itemId, reFetch: true },
                );

                await this.loadOne({ reFetch: true, items: true, id: groupId });

                if (previousGroupId) {
                    await this.loadOne({ reFetch: true, items: true, id: previousGroupId });
                }
            } catch (savingError) {
                this.setError(savingError);
                throw savingError;
            } finally {
                this.setSaving(false);
            }
        },
        async removeItemFromGroup (payload: { groupId: string; itemType: string; itemId: string } = { groupId: '', itemType: '', itemId: '' }) {
            this.setSaving(true);
            const { groupId, itemType, itemId } = payload;
            if (!groupId) return;

            // eslint-disable-next-line @typescript-eslint/no-require-imports
            const vuexStore = require('@/vuex/store').default;
            try {
                await removeItemFromGroup({ groupId, itemType, itemId });
                await vuexStore?.dispatch(
                    `${itemTypeToModuleName[itemType]}/loadOne`,
                    { id: itemId, reFetch: true },
                );

                await this.loadOne({ reFetch: true, items: true, id: groupId });
            } catch (savingError) {
                this.setError(savingError);
                throw savingError;
            } finally {
                this.setSaving(false);
            }
        },
        updateFilter ({ idFilter }: { idFilter: string[] }) {
            this.setFilter({ idFilter });

            useUserSettingsStore().update({
                namespaced: true,
                name: 'groupFilter',
                value: idFilter,
            });
        },
        clearFilter () {
            this.updateFilter({ idFilter: [] });
        },
        teardown () {
            this.$reset();
            loadAllPromise = null;
        },
    },
});

export function __resetLoadAllPromiseForUnitTests () {
    loadAllPromise = null;
}

if (module.hot) {
    module.hot.accept(acceptHMRUpdate(useGroupsStore, module.hot));
}
